import { IconButton } from '@/components/IconButton/IconButton';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import CloseIcon from '@/images/CloseIconNew.svg?react';
import { Box } from '@/components/Box/Box';
import { Body, Content, Footer, Header } from './styles';
import { CookiesPreferencesSection } from './CookiesPreferencesSection';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { useFormik } from 'formik';
import { CookiesManagePreferencesModalWindow } from './CookiesManagePreferencesModalWindow';
import { Button } from '@/components/Button/Button';
import { CookiesPreferencesType, DEFAULT_COOKIES_PREFERENCES } from '@/bundle/CookiesBanner/cookiesContext';

type CookiesManagerPreferencesModalType = {
  preferences?: CookiesPreferencesType | null;
  isOpen: boolean;
  onSave: (newPreference: CookiesPreferencesType) => void;
  onClose: () => void;
};

export const CookiesManagePreferencesModal = ({
  preferences,
  isOpen,
  onSave,
  onClose,
}: CookiesManagerPreferencesModalType) => {
  const initialValues = preferences ? { ...preferences } : { ...DEFAULT_COOKIES_PREFERENCES };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit(values: CookiesPreferencesType) {
      onSave(values);
    },
  });

  return (
    <CookiesManagePreferencesModalWindow isOpen={isOpen}>
      <Header>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='headline4' color={COLORS.black} fontWeight='semibold'>
            Manage Consent Preferences
          </Typography>
          <IconButton onClick={onClose} svgPath='stroke'>
            <CloseIcon />
          </IconButton>
        </Box>
      </Header>
      <FormikForm value={formik}>
        <Body>
          <Content>
            <Typography>
              CrediVault and our trusted partners use cookies and similar technologies to access and use your data for
              the purposes listed below. Please provide your consent for cookie usage on this website. Enable one or
              more of the cookie types listed below, and then save your preferences.
            </Typography>

            <Box display='flex' flexDirection='column' rowGap='26px' mobile={{ rowGap: '22px' }}>
              <CookiesPreferencesSection label='Strictly Necessary Cookies' name='strictly_cookies_accepted' disabled>
                These cookies are necessary for the website and app to function and cannot be switched off in our
                systems. They are usually only set in response to actions made by you which amount to a request for
                services, such as setting your privacy preferences, logging in or filling in forms. You can set your
                browser to block or alert you about these cookies, but some parts of the website and app will not then
                work. These cookies do not store any personally identifiable information.
                <br />
                <br />
                CrediVault uses Google reCAPTCHA v2 to authenticate human users and to prevent spam and abuse by
                automated bots or programs. ReCAPTCHA v2 collects information regarding user IP addresses, browser
                history/plug-ins, behavior (like scrolling, clicking on links, typing patterns, etc.), cookies, and CSS
                information. For website visitors from outside the United States, completing reCAPTCHA v2 on our website
                may result in the above information transferring to servers in the United States. We use reCAPTCHA where
                necessary to authenticate visitors who seek to create or login to an account and access the paid
                CrediVault features.
              </CookiesPreferencesSection>

              <CookiesPreferencesSection label='Functional Cookies' name='functional_cookies_accepted'>
                These cookies enable the website and app to provide enhanced functionality and personalization. They may
                be set up by us or by third party providers whose services we have added to our pages. If you do not
                allow these cookies, then some or all of these services may not function properly.
              </CookiesPreferencesSection>

              <CookiesPreferencesSection label='Performance Cookies' name='performance_cookies_accepted'>
                These cookies allow us to count visits and traffic sources so we can measure and improve the performance
                of our site and app. They help us to know which pages are the most and least popular and see how
                visitors move around the site and app. All the information these cookies collect is aggregated and
                therefore anonymous. If you do not allow these cookies we will not know when you have visited our
                website or app and will not be able to monitor its performance.
              </CookiesPreferencesSection>

              <CookiesPreferencesSection label='Targeting Cookies' name='targeting_cookies_accepted'>
                These cookies may be set through our site or app by our partners. They may be used by those companies to
                build a profile of your interests and show you relevant adverts on other sites. They do not store
                directly personal information, but are based on uniquely identifying your browser and internet device.
                If you do not allow these cookies, you will experience less targeted advertising.
                <br />
                <br />
                Third party cookies may include social media cookies designed to show you ads and content based on your
                social media profiles and activities on our website and advertising cookies to show you ads that are
                relevant to you.
              </CookiesPreferencesSection>
            </Box>
          </Content>

          <Footer>
            <Button width={220} mobileStretch type='submit'>
              Save My Preferences
            </Button>
          </Footer>
        </Body>
      </FormikForm>
    </CookiesManagePreferencesModalWindow>
  );
};
